import React from 'react';
import PropTypes from 'prop-types';
import html2canvas from 'html2canvas';
import JsPDF from 'jspdf';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Box, Grid, Text } from 'grommet';
import Case from 'case';
import { Lock } from 'grommet-icons';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useAppTheme } from '@Components/Context';
import {
  Seo, Spinning, ScrollToTopButton, FlexibleSelectDropdown,
} from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import {
  MetricCard,
  DomainTrafficChart,
  SearchEnginePieChart,
  ProviderBarChart,
  GeoHeatmapPanel,
  InfringementsPerEventChart,
  SocialMediaBarChart,
  GeoTrafficDistribution,
  PostEventSummaryCards,
  ExecutiveTakeawaysPanel,
  PlatformBreakdownChart,
} from '@Components/Partial/Product/Analytics';
import { TableWrapper } from '@Components/Partial/Product/SharedComponents.jsx';
import {
  initiateResultsInfringementsRequest,
  clearResultsInfringementsRequest,
  initiateLiveTrackerAnalyticsRequest,
  clearLiveTrackerAnalyticsRequest,
  initiateWatermarkDetectionsRequest,
  clearWatermarkDetectionsRequest,
} from '@Actions';
import {
  StyledChartWrap,
  StyledPanel,
  StyledScrollPanel,
  StyledTable,
  StyledPagedTableUrl,
} from './StyledLiveTrackerPage';
import * as utils from './liveTrackerUtil';

const LiveTrackerPage = ({
  small,
  location,
  infringementsData = null,
  hostingData = null,
  watermarkDetectionsData = null,
  loading,
  hostingDataLoading,
  managementReportsAccess,
  fetchInfringements,
  clearInfringements,
  fetchHostingData,
  clearHostingData,
  fetchWatermarkDetections,
  clearWatermarkDetections,
  featureConfig,
}) => {
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const [activeTab, setActiveTab] = React.useState('pre-event');
  const [selectedEventTitles, setSelectedEventTitles] = React.useState([]);
  const [fullReviewRows, setFullReviewRows] = React.useState([]);
  const [fullReviewHeaders, setFullReviewHeaders] = React.useState([]);
  const [isExporting, setIsExporting] = React.useState(false);
  const contentRef = React.useRef(null);
  const pdfHeaderRef = React.useRef(null);

  React.useEffect(() => {
    fetchInfringements(
      {},
      false,
      (response) => {
        const reviewWidget = response?.data?.find(({ type }) => type === 'review-table');
        if (reviewWidget) {
          const rawRows = reviewWidget.value?.rows || [];
          setFullReviewRows(rawRows.map((row) => (Array.isArray(row) ? row : (row.cells || row))));
          setFullReviewHeaders(reviewWidget.value?.header || []);
        }
      },
    );
    fetchHostingData();
    fetchWatermarkDetections();
    return () => {
      clearInfringements();
      clearHostingData();
      clearWatermarkDetections();
    };
  }, []);

  const {
    EVENT_TABS,
    LOCKED_PANELS,
    DOMAIN_HEADER_MAP,
    TOP_SITES_HEADER_MAP,
  } = utils.getLiveTrackerConstants();
  const useSubscriptionFee = featureConfig?.liveTrackerEstimatedSubscriptionFee
    ?? utils.ESTIMATE_SUBSCRIPTION_FEE;
  const watermarkGroupingKey = featureConfig?.liveTrackerWatermarkGroupingKey
    ? Case.camel(featureConfig.liveTrackerWatermarkGroupingKey)
    : null;
  const showPrePostEventAnalysis = featureConfig?.showPrePostEventAnalysis ?? true;
  const showManagementReport = !!watermarkGroupingKey && !!managementReportsAccess;

  const visibleTabs = EVENT_TABS.filter((tab) => {
    if (tab.key === 'management-report') return showManagementReport;
    if (tab.key === 'pre-event' || tab.key === 'post-event') return showPrePostEventAnalysis;
    return true;
  });

  React.useEffect(() => {
    if (activeTab === 'management-report' && !showManagementReport) {
      setActiveTab(showPrePostEventAnalysis ? 'pre-event' : 'event-day');
    } else if (!showPrePostEventAnalysis && activeTab !== 'event-day' && activeTab !== 'management-report') {
      setActiveTab('event-day');
    }
  }, [showPrePostEventAnalysis, showManagementReport]);

  // Find review table widget and headers
  const reviewWidget = React.useMemo(
    () => infringementsData?.data?.find(({ type }) => type === 'review-table'),
    [infringementsData],
  );
  const reviewHeaders = React.useMemo(
    () => reviewWidget?.value?.header || [],
    [reviewWidget],
  );
  const reviewRows = React.useMemo(() => {
    const rawRows = reviewWidget?.value?.rows || [];
    return rawRows.map((row) => (Array.isArray(row) ? row : (row.cells || row)));
  }, [reviewWidget]);

  const eventDateTimestamp = React.useMemo(
    () => utils.extractEventDatetimeFromHeader(infringementsData, reviewWidget),
    [infringementsData, reviewWidget],
  );

  // Extract event title options from unfiltered review rows
  const eventTitleOptions = React.useMemo(
    () => utils.getEventTitleOptions(fullReviewRows, fullReviewHeaders),
    [fullReviewRows, fullReviewHeaders],
  );

  const eventCountValue = selectedEventTitles.length > 0
    ? utils.formatInt(selectedEventTitles.length)
    : utils.formatInt(eventTitleOptions.length);

  const handleEventTitleFilter = (newTitles) => {
    setSelectedEventTitles(newTitles);
    const filters = newTitles.length
      ? [{ name: 'title_multi', value: newTitles, predicate: 'in' }]
      : [];
    fetchInfringements({ filters });
  };

  const classifiedRows = React.useMemo(
    () => (showPrePostEventAnalysis
      ? utils.classifyRowsByEvent(reviewRows, reviewHeaders, eventDateTimestamp)
      : { preEvent: [], eventDay: reviewRows, postEvent: [] }),
    [showPrePostEventAnalysis, reviewRows, reviewHeaders, eventDateTimestamp],
  );

  const activeRows = React.useMemo(() => {
    if (activeTab === 'pre-event') return classifiedRows.preEvent;
    if (activeTab === 'event-day') return classifiedRows.eventDay;
    return reviewRows;
  }, [activeTab, classifiedRows, reviewRows]);

  const allFirstDetectedByDomain = React.useMemo(
    () => utils.getAllFirstDetectedByDomain(reviewRows, reviewHeaders),
    [reviewRows, reviewHeaders],
  );

  const hostMetricsRows = React.useMemo(
    () => hostingData?.data || [],
    [hostingData],
  );
  const preEventRows = React.useMemo(
    () => classifiedRows.preEvent,
    [classifiedRows.preEvent],
  );
  const eventDayRows = React.useMemo(
    () => classifiedRows.eventDay,
    [classifiedRows.eventDay],
  );
  const preAndEventRows = React.useMemo(
    () => [...classifiedRows.preEvent, ...classifiedRows.eventDay],
    [classifiedRows.preEvent, classifiedRows.eventDay],
  );

  const preEventStats = React.useMemo(
    () => utils.buildTrackerStats(
      preEventRows,
      preEventRows.length,
      hostMetricsRows,
      reviewHeaders,
      allFirstDetectedByDomain,
      0.2,
      useSubscriptionFee,
      eventTitleOptions,
    ),
    [
      preEventRows,
      hostMetricsRows,
      reviewHeaders,
      allFirstDetectedByDomain,
      useSubscriptionFee,
      eventTitleOptions,
    ],
  );

  const eventDayStats = React.useMemo(
    () => utils.buildTrackerStats(
      eventDayRows,
      eventDayRows.length,
      hostMetricsRows,
      reviewHeaders,
      allFirstDetectedByDomain,
      0.2,
      useSubscriptionFee,
      eventTitleOptions,
    ),
    [
      eventDayRows,
      hostMetricsRows,
      reviewHeaders,
      allFirstDetectedByDomain,
      useSubscriptionFee,
      eventTitleOptions,
    ],
  );

  const preAndEventStats = React.useMemo(
    () => utils.mergeStats(preEventStats, eventDayStats),
    [preEventStats, eventDayStats],
  );

  // trackerStats is always based on activeRows
  const trackerStats = React.useMemo(
    () => utils.buildTrackerStats(
      activeRows,
      activeRows.length,
      hostMetricsRows,
      reviewHeaders,
      allFirstDetectedByDomain,
      0.2,
      useSubscriptionFee,
      eventTitleOptions,
    ),
    [
      activeRows,
      hostMetricsRows,
      reviewHeaders,
      allFirstDetectedByDomain,
      useSubscriptionFee,
      eventTitleOptions,
    ],
  );

  const searchEngineData = React.useMemo(
    () => [
      { source: 'Google', urls: trackerStats.googleUrls },
      { source: 'Bing', urls: trackerStats.bingUrls },
    ],
    [trackerStats.googleUrls, trackerStats.bingUrls],
  );

  const providerBarData = React.useMemo(
    () => utils.getProviderBarData(trackerStats.hostingProviderStats, utils.shortLabel),
    [trackerStats.hostingProviderStats],
  );

  const trafficSeriesData = React.useMemo(
    () => utils.getTrafficSeriesData(trackerStats.topVisitedSites, utils.shortLabel),
    [trackerStats.topVisitedSites],
  );

  const geographyData = React.useMemo(
    () => utils.getGeographyData(trackerStats.countryViewerDistribution, utils.shortLabel),
    [trackerStats.countryViewerDistribution],
  );

  const usIntlData = React.useMemo(
    () => trackerStats.usIntlDistribution,
    [trackerStats.usIntlDistribution],
  );

  const detectionChartData = React.useMemo(
    () => utils.getDetectionChartData(trackerStats.detectionByDomain, utils.shortLabel),
    [trackerStats.detectionByDomain],
  );

  const topSitesByInfringementsData = React.useMemo(
    () => utils.getTopSitesByInfringementsData(trackerStats.domainBreakdown, utils.shortLabel),
    [trackerStats.domainBreakdown],
  );

  const postEventAllPlatformBreakdownData = React.useMemo(
    () => utils.getPostEventAllPlatformBreakdownData(
      preAndEventRows,
      reviewHeaders,
      utils.shortLabel,
      utils.getPlatformFromRow,
    ),
    [preAndEventRows, reviewHeaders],
  );

  const postEventLinksGeoHeatmapData = React.useMemo(
    () => preAndEventStats.hydratedDomainStats.reduce(
      (acc, item) => {
        const current = { ...acc };
        const code = utils.getCountryCodeFromLocation(item.ipGeolocation);
        if (!code) return current;
        current[code] = (current[code] || 0) + item.count;
        return current;
      },
      {},
    ),
    [preAndEventStats.hydratedDomainStats],
  );

  const socialPlatformBreakdownData = React.useMemo(
    () => utils.getSocialPlatformBreakdownData(
      activeRows,
      reviewHeaders,
      utils.shortLabel,
      utils.getPlatformFromRow,
    ),
    [activeRows, reviewHeaders],
  );

  const highlightBoxShadow = React.useMemo(
    () => utils.getHighlightBoxShadow({ isDarkMode }),
    [isDarkMode],
  );

  const watermarkHasInfo = React.useMemo(
    () => utils.hasWatermarkInfo(reviewRows, watermarkGroupingKey),
    [reviewRows, watermarkGroupingKey],
  );

  const watermarkGroupStats = React.useMemo(
    () => (watermarkGroupingKey
      ? utils.buildWatermarkGroupStats(eventDayRows, watermarkGroupingKey)
      : []),
    [eventDayRows, watermarkGroupingKey],
  );

  const combinedWatermarkGroupStats = React.useMemo(() => {
    const groups = {};
    watermarkGroupStats.forEach((item) => {
      groups[item.key] = { key: item.key, infringements: item.infringements };
    });
    watermarkDetectionsData?.forEach((d) => {
      if (!d.identified_source) return;
      if (!groups[d.identified_source]) {
        groups[d.identified_source] = { key: d.identified_source, infringements: 0 };
      }
      groups[d.identified_source].infringements += 1;
    });
    return Object.values(groups).sort((a, b) => b.infringements - a.infringements);
  }, [watermarkGroupStats, watermarkDetectionsData]);

  const watermarkValuesByDomain = React.useMemo(
    () => (watermarkGroupingKey
      ? utils.getWatermarkValuesByDomain(eventDayRows, watermarkGroupingKey)
      : {}),
    [eventDayRows, watermarkGroupingKey],
  );

  const watermarkLinkRows = React.useMemo(
    () => (watermarkGroupingKey
      ? utils.buildWatermarkLinkRows(eventDayRows, watermarkGroupingKey)
      : []),
    [eventDayRows, watermarkGroupingKey],
  );

  const preEventInfringementsByDay = React.useMemo(() => {
    const days = 7;
    const base = Math.max(1, Math.round(trackerStats.totalInfringingLinks / days));
    return Array.from({ length: days }, (_, i) => ({
      day: `Day ${i + 1}`,
      infringements: base + Math.round(Math.random() * base * 0.5),
    }));
  }, [trackerStats.totalInfringingLinks]);

  // KPI and spike calculations
  const kpiSummary = React.useMemo(
    () => utils.getKpiAndExecutiveSummary({
      trackerStats,
      preEventStats,
      eventDayStats,
      utils,
    }),
    [trackerStats, preEventStats, eventDayStats, utils],
  );

  const {
    kpiStats,
    kpiGooglePercentage,
    urlSpikePct,
    trafficSpikePct,
    domainsSpikePct,
    top3ProviderTrafficPct,
    piracyConcentration,
    leverageProviders,
    chokePointDomains,
  } = kpiSummary;

  // Pre-event demand modeling
  const uniqueLinksFound = trackerStats.distinctUrlCount;
  const avgEstViewersPerLink = uniqueLinksFound
    ? Math.round(trackerStats.aggregateConcurrentEstimate / uniqueLinksFound)
    : 0;
  const totalEstViewersLow = Math.round(trackerStats.aggregateConcurrentEstimate * 0.75);
  const totalEstViewersHigh = Math.round(trackerStats.aggregateConcurrentEstimate * 1);
  const preEventRevenueRiskLow = trackerStats.estimatedLossRevenue * 0.75;
  const preEventRevenueRiskHigh = trackerStats.estimatedLossRevenue * 1;
  const postEventRevenueLossRangeLow = preAndEventStats.estimatedLossRevenue * 0.75;
  const postEventRevenueLossRangeHigh = preAndEventStats.estimatedLossRevenue * 1;

  const renderUrl = (value) => <StyledPagedTableUrl color="var(--page-url-color)" href={value} target="_blank">{value}</StyledPagedTableUrl>;

  const useActiveSliderBg = isDarkMode ? page.highlight : 'white';

  const exportToPDF = async () => {
    if (!contentRef.current) return;
    setIsExporting(true);

    const element = contentRef.current;
    const scrollPanels = element.querySelectorAll('[data-scroll-panel]');
    const savedStyles = Array.from(scrollPanels).map((el) => ({
      el,
      maxHeight: el.style.maxHeight,
      overflowY: el.style.overflowY,
    }));
    scrollPanels.forEach((el) => {
      el.style.maxHeight = 'none';
      el.style.overflowY = 'visible';
    });

    try {
      const headerCaptureOpts = { scale: 2, useCORS: true, allowTaint: true };

      // Chrome silently produces a blank canvas when either dimension exceeds ~32767px.
      // Reduce scale proportionally so canvas height stays safely under that limit.
      const MAX_CANVAS_DIM = 30000;
      const rawH = element.scrollHeight;
      const contentScale = rawH > 0 ? Math.min(2, MAX_CANVAS_DIM / rawH) : 2;
      const contentCaptureOpts = { scale: contentScale, useCORS: true, allowTaint: true };

      if (pdfHeaderRef.current) {
        pdfHeaderRef.current.style.width = `${element.scrollWidth}px`;
      }

      const [headerCanvas, contentCanvas] = await Promise.all([
        pdfHeaderRef.current
          ? html2canvas(pdfHeaderRef.current, headerCaptureOpts)
          : Promise.resolve(null),
        html2canvas(element, {
          ...contentCaptureOpts,
          scrollX: 0,
          scrollY: 0,
          width: element.scrollWidth,
          height: rawH,
        }),
      ]);

      const pdf = new JsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const headerImgHeight = headerCanvas
        ? (headerCanvas.height * pageWidth) / headerCanvas.width
        : 0;
      const contentImgHeight = (contentCanvas.height * pageWidth) / contentCanvas.width;
      const contentImgData = contentCanvas.toDataURL('image/jpeg', 0.92);

      if (headerCanvas) {
        pdf.addImage(headerCanvas.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pageWidth, headerImgHeight);
      }
      pdf.addImage(contentImgData, 'JPEG', 0, headerImgHeight, pageWidth, contentImgHeight);

      let remaining = contentImgHeight - (pageHeight - headerImgHeight);
      let pdfPage = 1;
      while (remaining > 0) {
        pdf.addPage();
        pdf.addImage(contentImgData, 'JPEG', 0, headerImgHeight - pdfPage * pageHeight, pageWidth, contentImgHeight);
        remaining -= pageHeight;
        pdfPage += 1;
      }

      pdf.save(`live-tracker-${activeTab}.pdf`);
    } finally {
      savedStyles.forEach(({ el, maxHeight, overflowY }) => {
        el.style.maxHeight = maxHeight;
        el.style.overflowY = overflowY;
      });
      setIsExporting(false);
    }
  };

  const managementReportData = React.useMemo(
    () => utils.buildManagementReport(reviewRows, reviewHeaders, watermarkGroupingKey),
    [reviewRows, reviewHeaders, watermarkGroupingKey],
  );

  const totalUniqueMatchdays = new Set();

  const affiliateConfidenceMap = React.useMemo(() => {
    const map = {};
    (watermarkDetectionsData || []).forEach((d) => {
      if (!d.identified_source) return;

      // eslint-disable-next-line camelcase
      if (!d.match_confidence) d.match_confidence = 0;

      const src = d.identified_source;
      if (!map[src]) map[src] = { total: 0, count: 0 };
      map[src].total += d.match_confidence;
      map[src].count += 1;
    });
    return Object.fromEntries(
      Object.entries(map).map(([name, { total, count }]) => [
        name,
        Math.round(total / count),
      ]),
    );
  }, [watermarkDetectionsData]);

  const managementReportTotals = React.useMemo(() => {
    const linkInfringements = managementReportData.reduce((s, e) => s + e.totalInfringements, 0);
    const totalDomains = managementReportData.reduce((s, e) => s + e.totalUniqueDomains, 0);

    // Build per-affiliate link stats with a set of event names for match weekend counting
    const affiliateMap = {};
    managementReportData.forEach((event) => {
      event.affiliates.forEach((aff) => {
        if (!affiliateMap[aff.name]) {
          affiliateMap[aff.name] = {
            name: aff.name,
            infringements: 0,
            uniqueDomains: 0,
            weekendSet: new Set(),
            detectedFiles: 0,
          };
        }
        affiliateMap[aff.name].infringements += aff.infringements;
        affiliateMap[aff.name].uniqueDomains += aff.uniqueDomains;
        affiliateMap[aff.name].weekendSet.add(event.matchday);
        totalUniqueMatchdays.add(event.matchday);
      });
    });

    // Per-event file counts: { eventName: { affiliateName: count } }
    const perEventFileStats = {};
    let totalDetectedFiles = 0;
    let totalMatchedFiles = 0;

    (watermarkDetectionsData || []).forEach((d) => {
      totalDetectedFiles += 1;
      if (d.is_match_detected) totalMatchedFiles += 1;
      if (!d.identified_source) return;

      const src = d.identified_source;

      if (!affiliateMap[src]) {
        affiliateMap[src] = {
          name: src, infringements: 0, uniqueDomains: 0, weekendSet: new Set(), detectedFiles: 0,
        };
      }
      affiliateMap[src].detectedFiles += 1;
      if (d.match_day) {
        affiliateMap[src].weekendSet.add(d.match_day || 'Unknown Match Day');
        totalUniqueMatchdays.add(d.match_day || 'Unknown Match Day');
        if (!perEventFileStats[d.match_day]) perEventFileStats[d.match_day] = {};
        perEventFileStats[d.match_day][src] = (perEventFileStats[d.match_day][src] || 0) + 1;
      }
    });

    const totalAffiliateBreakdown = Object.values(affiliateMap)
      .map((aff) => ({ ...aff, matchWeekends: aff.weekendSet.size }))
      .sort((a, b) => b.infringements - a.infringements);


    const mergedEventData = Array.from(totalUniqueMatchdays).map((matchday) => {
      const reportData = managementReportData.find((e) => e.matchday === matchday);
      const fileData = perEventFileStats[matchday] || {};

      const totalInfringements = (reportData ? reportData.totalInfringements : 0)
    + Object.values(fileData).reduce((s, c) => s + c, 0);

      const totalUniqueDomains = reportData ? reportData.totalUniqueDomains : 0;

      const affiliatesMap = new Map();

      // Add reportData affiliates
      (reportData?.affiliates || []).forEach((a) => {
        affiliatesMap.set(a.name, {
          name: a.name,
          infringements: a.infringements,
          uniqueDomains: a.uniqueDomains,
        });
      });

      // Merge fileData
      Object.entries(fileData).forEach(([name, count]) => {
        if (affiliatesMap.has(name)) {
          affiliatesMap.get(name).infringements += count;
        } else {
          affiliatesMap.set(name, {
            name,
            infringements: count,
            uniqueDomains: 0,
          });
        }
      });

      const affiliates = Array.from(affiliatesMap.values()).sort(
        (a, b) => b.infringements - a.infringements,
      );

      return {
        matchday,
        totalInfringements,
        totalUniqueDomains,
        affiliatesIdentified: affiliates.length,
        affiliates,
      };
    });
    mergedEventData.sort((a, b) => a.matchday.localeCompare(b.matchday));

    return {
      matchWeekends: totalUniqueMatchdays.size,
      totalAffiliates: totalAffiliateBreakdown.length,
      totalInfringements: linkInfringements + totalDetectedFiles,
      totalDomains,
      totalMatchedFiles,
      totalAffiliateBreakdown,
      mergedEventData,
    };
  }, [managementReportData, watermarkDetectionsData]);

  // Loader and main content
  const renderPageContents = () => {
    if (
      loading
      || hostingDataLoading
      || !infringementsData
    ) {
      return (
        <Box flex align="center" justify="center" background={page.background}>
          <Spinning size="large" color={page.highlight} />
        </Box>
      );
    }

    // Tab logic
    const isEventDay = activeTab === 'event-day';
    const isPreEvent = activeTab === 'pre-event';
    const isPostEvent = activeTab === 'post-event';
    const isManagementReport = activeTab === 'management-report';
    const showWatermark = isEventDay && !!watermarkGroupingKey && watermarkHasInfo;

    if (isManagementReport) {
      const affiliateKeyLabel = watermarkGroupingKey
        ? utils.camelToLabel(watermarkGroupingKey)
        : 'Affiliate';

      return (
        <Box
          ref={contentRef}
          flex
          pad={{ horizontal: small ? '0.75rem' : '2rem', vertical: small ? '1rem' : '2rem' }}
          background={isDarkMode ? page.background : page.altBg}
          direction="column"
          gap="1rem"
        >
          {/* Summary KPIs */}
          <Grid columns={small ? ['1fr', '1fr'] : ['1fr', '1fr', '1fr', '1fr', '1fr']} gap="1rem">
            <MetricCard
              large
              label="MATCH WEEKENDS"
              value={utils.formatInt(managementReportTotals.matchWeekends)}
              textColor={page.primaryText}
              subLabel="Distinct matchdays across all infringement records"
              subLabelColor={page.hintText}
              boxShadow={highlightBoxShadow}
              borderColor={page.highlight}
            />
            <MetricCard
              large
              label={`IDENTIFIED ${affiliateKeyLabel.toUpperCase()}S`}
              value={watermarkGroupingKey
                ? utils.formatInt(managementReportTotals.totalAffiliates)
                : 'N/A'}
              textColor={page.primaryText}
              subLabel={watermarkGroupingKey
                ? `Unique ${affiliateKeyLabel.toLowerCase()}s across links and watermarked files`
                : `Enable ${affiliateKeyLabel.toLowerCase()} detection to see affiliate data`}
              subLabelColor={page.hintText}
              boxShadow={highlightBoxShadow}
              borderColor={page.highlight}
            />
            <MetricCard
              large
              label="TOTAL INFRINGEMENTS"
              value={utils.formatInt(managementReportTotals.totalInfringements)}
              textColor={page.primaryText}
              subLabel="Infringments across all match weekends"
              subLabelColor={page.hintText}
              boxShadow={highlightBoxShadow}
              borderColor={page.highlight}
            />
            <MetricCard
              large
              label="TOTAL UNIQUE DOMAINS"
              value={utils.formatInt(managementReportTotals.totalDomains)}
              textColor={page.primaryText}
              subLabel="Distinct domains hosting infringing content across all events"
              subLabelColor={page.hintText}
              boxShadow={highlightBoxShadow}
              borderColor={page.highlight}
            />
            <MetricCard
              large
              label="WATERMARK MATCHES"
              value={utils.formatInt(managementReportTotals.totalMatchedFiles)}
              textColor={page.primaryText}
              subLabel="Files where a watermark match was confirmed"
              subLabelColor={page.hintText}
              boxShadow={highlightBoxShadow}
              borderColor={page.highlight}
            />
          </Grid>

          {/* Total breakdown by affiliate across all events */}
          {managementReportTotals.totalAffiliateBreakdown.length > 0 && (
            <StyledPanel gap="1rem">
              <Box direction="column" gap="0.25rem">
                <Text color={page.primaryText} size="1rem" weight={700}>
                  {`Total Breakdown by ${affiliateKeyLabel}`}
                </Text>
                <Text color={page.secondaryText} size="0.8rem">
                  {`Aggregate infringements and domains per ${affiliateKeyLabel.toLowerCase()} across all match weekends.`}
                </Text>
              </Box>
              <StyledScrollPanel>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>{affiliateKeyLabel}</th>
                      <th>Total Infringements</th>
                      <th>Unique Domains</th>
                      <th>Match Weekends Active</th>
                      <th>Avg. Match Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managementReportTotals.totalAffiliateBreakdown.map((aff) => (
                      <tr key={aff.name}>
                        <td>{aff.name}</td>
                        <td>{utils.formatInt(aff.infringements + aff.detectedFiles)}</td>
                        <td>{utils.formatInt(aff.uniqueDomains)}</td>
                        <td>{utils.formatInt(aff.matchWeekends)}</td>
                        <td>
                          {affiliateConfidenceMap[aff.name] != null
                            ? `${affiliateConfidenceMap[aff.name]}%`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </StyledScrollPanel>
            </StyledPanel>
          )}

          {/* Per-match-weekend breakdown */}
          {managementReportTotals.mergedEventData.length === 0 && (
            <StyledPanel>
              <Text color={page.secondaryText} size="0.9rem">No infringement data available yet.</Text>
            </StyledPanel>
          )}
          {managementReportTotals.mergedEventData.map((event) => (
            <StyledPanel key={event.matchday} gap="1rem">
              <Box direction="column" gap="0.25rem">
                <Text color={page.primaryText} size="1rem" weight={700}>{event.matchday}</Text>
                <Text color={page.secondaryText} size="0.8rem">
                  {`${utils.formatInt(event.totalInfringements)} infringement${event.totalInfringements !== 1 ? 's' : ''} across ${utils.formatInt(event.totalUniqueDomains)} unique domain${event.totalUniqueDomains !== 1 ? 's' : ''}`}
                </Text>
              </Box>
              <Grid columns={small ? ['1fr', '1fr'] : ['1fr', '1fr', '1fr']} gap="1rem">
                <MetricCard
                  label={`${affiliateKeyLabel.toUpperCase()}S`}
                  value={watermarkGroupingKey
                    ? utils.formatInt(event.affiliatesIdentified)
                    : 'N/A'}
                  textColor={page.primaryText}
                  subLabel={watermarkGroupingKey
                    ? `Distinct ${affiliateKeyLabel.toLowerCase()} sources`
                    : 'No affiliate detection configured'}
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
                <MetricCard
                  label="TOTAL INFRINGEMENTS"
                  value={utils.formatInt(event.totalInfringements)}
                  textColor={page.primaryText}
                  subLabel="Infringement records for this match weekend"
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
                <MetricCard
                  label="UNIQUE DOMAINS"
                  value={utils.formatInt(event.totalUniqueDomains)}
                  textColor={page.primaryText}
                  subLabel="Distinct domains found for this match weekend"
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
              </Grid>
              {watermarkGroupingKey && event.affiliates.length > 0 && (
                <StyledScrollPanel style={{ maxHeight: '18rem' }}>
                  <StyledTable>
                    <thead>
                      <tr>
                        <th>{affiliateKeyLabel}</th>
                        <th>Total Infringements</th>
                        <th>Unique Domains</th>
                        <th>Avg. Match Confidence</th>
                      </tr>
                    </thead>
                    <tbody>
                      {event.affiliates.map((aff) => (
                        <tr key={aff.name}>
                          <td>{aff.name}</td>
                          <td>{utils.formatInt(aff.infringements)}</td>
                          <td>{utils.formatInt(aff.uniqueDomains)}</td>
                          <td>
                            {affiliateConfidenceMap[aff.name] != null
                              ? `${affiliateConfidenceMap[aff.name]}%`
                              : '—'}
                          </td>
                        </tr>
                      ))}
                      {event.unattributedInfringements > 0 && (
                        <tr>
                          <td style={{ fontStyle: 'italic', color: page.hintText }}>Unattributed</td>
                          <td>{utils.formatInt(event.unattributedInfringements)}</td>
                          <td>{utils.formatInt(event.unattributedDomains)}</td>
                          <td>—</td>
                        </tr>
                      )}
                    </tbody>
                  </StyledTable>
                </StyledScrollPanel>
              )}
              {watermarkGroupingKey && event.affiliates.length === 0 && (
                <Text color={page.hintText} size="0.8rem">
                  No affiliate attribution found for this match weekend.
                </Text>
              )}
              {!watermarkGroupingKey && (
                <Text color={page.hintText} size="0.8rem">
                  Configure an affiliate/watermark grouping key to enable per-affiliate breakdowns.
                </Text>
              )}
            </StyledPanel>
          ))}
        </Box>
      );
    }

    return (
      <Box
        ref={contentRef}
        flex
        pad={{ horizontal: small ? '0.75rem' : '2rem', vertical: small ? '1rem' : '2rem' }}
        background={isDarkMode ? page.background : page.altBg}
        direction="column"
        gap="1rem"
      >
        {/* KPI Cards */}
        <Box direction="column" gap="1rem">
          <MetricCard
            large
            label="TOTAL EVENTS"
            value={eventCountValue}
            textColor={page.primaryText}
            subLabel={utils.getMetricDescription('total events')}
            subLabelColor={page.hintText}
            boxShadow={highlightBoxShadow}
            borderColor={page.highlight}
            labelAddon={(
              <FlexibleSelectDropdown
                multiSelect
                withReset
                textColor={page.primaryText}
                flexibleOptions={eventTitleOptions}
                noSelectionLabel="Filter by event"
                displayKey="label"
                valueKey="value"
                selectedOption={selectedEventTitles}
                handleItemSelect={handleEventTitleFilter}
                buttonWidth="9rem"
                buttonJustify="between"
                buttonBg={page.altBg}
                borderColor={page.inputBorder}
                iconColor={page.inputIcon}
                iconPath="images/filter-input-icon.svg"
                overridePosition={{ top: 'bottom', left: 'left' }}
              />
            )}
          />
          <Grid columns={['1fr', '1fr', '1fr']} gap="1rem">
            <MetricCard
              large
              label="TOTAL INFRINGING LINKS"
              value={utils.formatInt(kpiStats.totalInfringingLinks)}
              textColor={page.primaryText}
              subLabel={utils.getMetricDescription('total infringing links')}
              subLabelColor={page.hintText}
            />
            <MetricCard
              large
              label="TOTAL UNIQUE DOMAINS"
              value={utils.formatInt(kpiStats.totalUniqueDomains)}
              textColor={page.primaryText}
              subLabel={utils.getMetricDescription('total unique domains')}
              subLabelColor={page.hintText}
            />
            <MetricCard
              large
              label="TOTAL UNIQUE ROOT DOMAINS"
              value={utils.formatInt(kpiStats.rootDomainCount)}
              textColor={page.primaryText}
              subLabel={utils.getMetricDescription('total unique root domains')}
              subLabelColor={page.hintText}
            />
          </Grid>
          <Grid columns={['1fr', '1fr']} gap="1rem">
            <MetricCard
              large
              label={`URLS FOUND ON GOOGLE (${kpiGooglePercentage}%)`}
              value={utils.formatInt(kpiStats.googleUrls)}
              textColor={page.primaryText}
              subLabel={utils.getMetricDescription('urls found on google')}
              subLabelColor={page.hintText}
            />
            <MetricCard
              large
              label={`URLS FOUND ON BING (${kpiStats.bingPercentage}%)`}
              value={utils.formatInt(kpiStats.bingUrls)}
              textColor={page.primaryText}
              subLabel={utils.getMetricDescription('urls found on bing')}
              subLabelColor={page.hintText}
            />
          </Grid>
          {isEventDay && (
            <Box direction="column" gap="1rem">
              <MetricCard
                large
                label="TOTAL VIEWERS"
                value={utils.displayOrUnavailable(
                  trackerStats.uniqueVisitorsCount, utils.formatInt,
                )}
                textColor={page.primaryText}
                subLabel={utils.getMetricDescription('total viewers')}
                subLabelColor={page.hintText}
                boxShadow={highlightBoxShadow}
                borderColor={page.highlight}
              />
              <MetricCard
                large
                label="ESTIMATED LOSS OF REVENUE"
                value={utils.displayOrUnavailable(
                  trackerStats.estimatedLossRevenue, utils.formatCurrency,
                )}
                textColor={page.primaryText}
                subLabel={utils.getMetricDescription('estimated loss of revenue', useSubscriptionFee)}
                subLabelColor={page.hintText}
                boxShadow={highlightBoxShadow}
                borderColor={page.highlight}
              />
              <MetricCard
                large
                label="TOTAL WATERMARK DETECTIONS"
                value={utils.formatInt(
                  watermarkLinkRows.length
                  + (watermarkDetectionsData?.filter((d) => d.identified_source)?.length || 0),
                )}
                subLabel={utils.getMetricDescription('total watermark detections', useSubscriptionFee)}
                textColor={page.primaryText}
                boxShadow={highlightBoxShadow}
                borderColor={page.highlight}
              />
            </Box>
          )}
        </Box>
        {showWatermark && (
          <StyledPanel gap="1rem">
            <Box direction="column" gap="0.35rem">
              <Text color={page.primaryText} size="1rem" weight={700}>
                Source Distribution of Detected Watermarks
              </Text>
              <Text color={page.secondaryText} size="0.8rem">
                All Infringements found grouped by
                {' '}
                <strong>{utils.camelToLabel(watermarkGroupingKey)}</strong>
                .
              </Text>
            </Box>
            <StyledScrollPanel>
              <StyledTable>
                <thead>
                  <tr>
                    <th>{utils.camelToLabel(watermarkGroupingKey)}</th>
                    <th>Infringements</th>
                  </tr>
                </thead>
                <tbody>
                  {combinedWatermarkGroupStats.map((item) => (
                    <tr key={item.key}>
                      <td>{item.key}</td>
                      <td>{utils.formatInt(item.infringements)}</td>
                    </tr>
                  ))}
                  {combinedWatermarkGroupStats.length === 0 && (
                    <tr>
                      <td colSpan={4}>No watermark data available.</td>
                    </tr>
                  )}
                </tbody>
              </StyledTable>
            </StyledScrollPanel>
          </StyledPanel>
        )}
        {showWatermark && watermarkLinkRows.length > 0 && (
          <StyledPanel gap="1rem">
            <Box direction="column" gap="0.35rem">
              <Text color={page.primaryText} size="1rem" weight={700}>
                Detected Watermarked Links
              </Text>
              <Text color={page.secondaryText} size="0.8rem">
                Infringements with
                {' '}
                {utils.getArticle(watermarkGroupingKey)}
                {' '}
                <strong>{utils.camelToLabel(watermarkGroupingKey)}</strong>
              </Text>
            </Box>
            <StyledScrollPanel>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Domain</th>
                    <th>URL</th>
                    <th>{utils.camelToLabel(watermarkGroupingKey)}</th>
                  </tr>
                </thead>
                <tbody>
                  {watermarkLinkRows.map((row, idx) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <tr key={idx}>
                      <td>{row.domain}</td>
                      <td style={{ wordBreak: 'break-all' }}>{renderUrl(row.url)}</td>
                      <td>{row.watermarkValue}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollPanel>
          </StyledPanel>
        )}
        {showWatermark && watermarkDetectionsData?.length > 0 && (
          <StyledPanel gap="1rem">
            <Box direction="column" gap="0.35rem">
              <Text color={page.primaryText} size="1rem" weight={700}>
                Watermark Analysis of Files
              </Text>
              <Text color={page.secondaryText} size="0.8rem">
                Files scanned for watermark matches and their detection results.
              </Text>
            </Box>
            <StyledScrollPanel>
              <StyledTable>
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Match Detected</th>
                    <th>Match Confidence</th>
                    <th>Identified Source</th>
                    <th>Language</th>
                    <th>Created At</th>
                  </tr>
                </thead>
                <tbody>
                  {watermarkDetectionsData.map((item) => (
                    // eslint-disable-next-line no-underscore-dangle
                    <tr key={item._id}>
                      <td>{item.file_name}</td>
                      <td>{item.result || (item.is_match_detected ? 'Yes' : 'No')}</td>
                      <td>{item.match_confidence ? `${item.match_confidence}%` : '—'}</td>
                      <td>{item.identified_source || '—'}</td>
                      <td>{item.meta_data?.language || '—'}</td>
                      <td>{utils.formatDateTime(item.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </StyledTable>
            </StyledScrollPanel>
          </StyledPanel>
        )}
        {/* Pre-Event Demand Modeling Section */}
        {isPreEvent && (
          <StyledPanel gap="1rem">
            <Box direction="column" gap="0.35rem">
              <Text color={page.primaryText} size="1rem" weight={700}>
                Pre-Event Demand Modeling
              </Text>
              <Text color={page.secondaryText} size="0.8rem">
                Early demand signal from unique links and modeled audience ranges.
              </Text>
            </Box>
            <Grid columns={small ? ['100%'] : ['1fr', '1fr', '1fr', '1fr']} gap="1rem">
              <MetricCard
                label="# UNIQUE LINKS FOUND"
                value={utils.formatInt(uniqueLinksFound)}
                textColor={page.primaryText}
                subLabel={utils.getMetricDescription('unique links found')}
                subLabelColor={page.hintText}
                cardAccent={page.altBg}
              />
              <MetricCard
                label="AVG EST. VIEWERS / LINK"
                value={utils.displayOrUnavailable(avgEstViewersPerLink)}
                textColor={page.primaryText}
                subLabel={utils.getMetricDescription('avg est. viewers / link')}
                subLabelColor={page.hintText}
                cardAccent={page.altBg}
              />
              <MetricCard
                label="TOTAL EST. VIEWERS"
                value={((totalEstViewersHigh === 0)
                  ? 'Data unavailable'
                  : `${utils.formatInt(totalEstViewersLow)} - ${utils.formatInt(totalEstViewersHigh)}`
                )}
                textColor={page.primaryText}
                subLabel={utils.getMetricDescription('total est. viewers')}
                subLabelColor={page.hintText}
                cardAccent={page.altBg}
              />
              <MetricCard
                label="EST. MONTHLY REVENUE AT RISK"
                value={((preEventRevenueRiskHigh === 0)
                  ? 'Data unavailable'
                  : `${utils.formatCurrency(preEventRevenueRiskLow)} - ${utils.formatCurrency(preEventRevenueRiskHigh)}`
                )}
                textColor={page.primaryText}
                subLabel={utils.getMetricDescription('revenue at risk', useSubscriptionFee)}
                subLabelColor={page.hintText}
                cardAccent={page.altBg}
              />
            </Grid>
            <StyledChartWrap>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={preEventInfringementsByDay}>
                  <defs>
                    <linearGradient id="preEventDemandGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#14B8A6" stopOpacity={0.65} />
                      <stop offset="95%" stopColor="#14B8A6" stopOpacity={0.08} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E6EEF6" />
                  <XAxis dataKey="day" tick={{ fill: '#5D738A', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#5D738A', fontSize: 11 }} />
                  <Tooltip {...utils.chartTooltip} formatter={utils.formatInt} />
                  <Area
                    type="monotone"
                    dataKey="infringements"
                    name="Increase in Infringements per Day"
                    stroke="#14B8A6"
                    fill="url(#preEventDemandGradient)"
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </StyledChartWrap>
          </StyledPanel>
        )}
        {!isPostEvent && socialPlatformBreakdownData.length > 0 && (
          <StyledPanel gap="1rem">
            <SocialMediaBarChart
              title="Social Media Platform Breakdown"
              subTitle={isEventDay ? 'Event Day infringing links found on non-Internet platforms, grouped by platform.' : 'Infringements detected on social media platforms.'}
              primaryText={page.primaryText}
              secondaryText={page.secondaryText}
              data={socialPlatformBreakdownData}
              chartTooltip={utils.chartTooltip}
              cardContent={(
                <MetricCard
                  label="TOTAL FOUND ON SOCIAL MEDIA PLATFORMS"
                  value={socialPlatformBreakdownData.reduce((s, i) => s + i.count, 0)}
                  textColor={page.primaryText}
                  subLabel={`Across ${socialPlatformBreakdownData.length} Social Media platform${socialPlatformBreakdownData.length !== 1 ? 's' : ''}`}
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
              )}
            />
          </StyledPanel>
        )}
        {/* Mirror Domains & Stream Resurfacing Rate + Piracy Audience Modeling */}
        {!isPostEvent && (
          <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">
            <StyledPanel gap="1rem">
              <Box direction="column" gap="0.35rem">
                <Text color={page.primaryText} size="1rem" weight={700}>
                  Mirror Domains &amp; Stream Resurfacing Rate
                </Text>
                <Text color={page.secondaryText} size="0.8rem">
                  % of domains reappearing under mirror URLs, modeled using sub-domain groups.
                </Text>
              </Box>
              <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">
                <MetricCard
                  label="ROOT DOMAINS"
                  value={utils.formatInt(trackerStats.rootDomainCount)}
                  textColor={page.primaryText}
                  subLabel={utils.getMetricDescription('root domains')}
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
                <MetricCard
                  label="STREAM RESURFACING RATE"
                  value={`${trackerStats.streamResurfacingRate}%`}
                  textColor={page.primaryText}
                  subLabel={utils.getMetricDescription('stream resurfacing rate')}
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
              </Grid>
              <StyledScrollPanel style={{ maxHeight: '20rem' }}>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>Root Domain</th>
                      <th>Mirror Sub-domains</th>
                      <th>Infringements</th>
                      {showWatermark && <th>{utils.camelToLabel(watermarkGroupingKey)}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {trackerStats.mirrorDomainFamilies.map((item) => (
                      <tr key={item.rootDomain}>
                        <td>{item.rootDomain}</td>
                        <td>{utils.formatInt(item.mirrorDomains)}</td>
                        <td>{utils.formatInt(item.infringements)}</td>
                        {showWatermark && (
                          <td>
                            {[...new Set(
                              item.domains.flatMap(
                                (d) => (Array.isArray(watermarkValuesByDomain[d])
                                  ? watermarkValuesByDomain[d] : []),
                              ),
                            )].join(', ') || '—'}
                          </td>
                        )}
                      </tr>
                    ))}
                    {trackerStats.mirrorDomainFamilies.length === 0 && (
                      <tr>
                        <td colSpan={3 + (showWatermark ? 1 : 0)}>
                          No mirror domain clusters detected yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </StyledTable>
              </StyledScrollPanel>
            </StyledPanel>
            <StyledPanel gap="1rem">
              <Box direction="column" gap="0.35rem">
                <Text color={page.primaryText} size="1rem" weight={700}>
                  Piracy Audience Modeling
                </Text>
                <Text color={page.secondaryText} size="0.8rem">
                  Estimated concurrent viewers per domain and per detected link/stream.
                </Text>
              </Box>
              <Grid columns={small ? ['100%'] : ['1fr', '1fr', '1fr']} gap="1rem">
                <MetricCard
                  label="AGGREGATE CONCURRENT ESTIMATE"
                  value={utils.displayOrUnavailable(
                    trackerStats.aggregateConcurrentEstimate, utils.formatInt,
                  )}
                  textColor={page.primaryText}
                  subLabel={utils.getMetricDescription('aggregate concurrent estimate')}
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
                <MetricCard
                  label="UNIQUE PIRATE VIEWER ESTIMATE"
                  value={utils.displayOrUnavailable(
                    trackerStats.uniquePirateViewerEstimate, utils.formatInt,
                  )}
                  textColor={page.primaryText}
                  subLabel={utils.getMetricDescription('unique pirate viewer estimate')}
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
                <MetricCard
                  label="MODELED TOTAL EVENT AUDIENCE"
                  value={utils.displayOrUnavailable(
                    trackerStats.modeledTotalEventAudience, utils.formatInt,
                  )}
                  textColor={page.primaryText}
                  subLabel={utils.getMetricDescription('modeled total event audience')}
                  subLabelColor={page.hintText}
                  cardAccent={page.altBg}
                />
              </Grid>
              <StyledScrollPanel style={{ maxHeight: '15rem' }}>
                <StyledTable>
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Links/Streams</th>
                      <th>Est. Concurrent / Domain</th>
                      <th>Est. Concurrent / Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trackerStats.viewerDomainEstimates.slice(0, 10).map((item) => (
                      <tr key={item.domain}>
                        <td>{item.domain}</td>
                        <td>{utils.formatInt(item.linksStreams)}</td>
                        <td>{utils.formatInt(item.estimatedConcurrentViewers)}</td>
                        <td>{utils.formatInt(item.estimatedConcurrentPerLink)}</td>
                      </tr>
                    ))}
                    {trackerStats.viewerDomainEstimates.length === 0 && (
                      <tr>
                        <td colSpan={4}>No audience modeling data available yet.</td>
                      </tr>
                    )}
                  </tbody>
                </StyledTable>
              </StyledScrollPanel>
              <Box gap="0.35rem">
                <Text color={page.hintText} size="0.7rem">
                  Methodology: concurrent estimate blends infringement
                  density with host traffic signals.
                </Text>
                <Text color={page.hintText} size="0.7rem">
                  Assumptions: 3.2x event turnover factor for unique
                  viewers and 1.15x expansion for modeled total audience.
                </Text>
              </Box>
            </StyledPanel>
          </Grid>
        )}
        {isEventDay && (
          <StyledPanel gap="1rem">
            <GeoTrafficDistribution
              small={small}
              geographyData={geographyData}
              usIntlData={usIntlData}
              primaryText={page.primaryText}
              secondaryText={page.secondaryText}
              formatInt={utils.formatInt}
              chartTooltip={utils.chartTooltip}
              cardContent={(
                <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="0.7rem" margin={{ bottom: '1rem' }}>
                  <MetricCard
                    label="EST. VISITORS IN U.S."
                    value={utils.displayOrUnavailable(
                      trackerStats.usVisitorsEstimate, utils.formatInt,
                    )}
                    subLabel={`${trackerStats.usVisitorsPercentage}% of total visitors`}
                    subLabelColor={page.hintText}
                    textColor={page.primaryText}
                    cardAccent={page.altBg}
                  />
                  <MetricCard
                    label="AVG VISITS IN U.S."
                    value={utils.displayOrUnavailable(
                      trackerStats.averageUsVisitorsEstimate, utils.formatInt,
                    )}
                    subLabelColor={page.hintText}
                    textColor={page.primaryText}
                    cardAccent={page.altBg}
                  />
                </Grid>
              )}
            />
          </StyledPanel>
        )}
        {isPostEvent && (
          <Box direction="column" gap="1rem">
            <StyledPanel gap="1rem">
              <Box direction="column" gap="0.35rem">
                <Text color={page.primaryText} size="1rem" weight={700}>
                  Post-Event Executive Summary
                </Text>
                <Text color={page.secondaryText} size="0.8rem">
                  Story-first totals from Pre-Event and Event Day,
                  with spike signals for executive reporting.
                </Text>
              </Box>
              <PostEventSummaryCards
                kpiStats={kpiStats}
                urlSpikePct={urlSpikePct}
                trafficSpikePct={trafficSpikePct}
                domainsSpikePct={domainsSpikePct}
                postEventRevenueLossRangeLow={postEventRevenueLossRangeLow}
                postEventRevenueLossRangeHigh={postEventRevenueLossRangeHigh}
                formatCurrency={utils.formatCurrency}
                highlightBoxShadow={highlightBoxShadow}
                textColor={page.primaryText}
                subLabelColor={page.secondaryText}
              />
            </StyledPanel>
            <StyledPanel gap="1rem">
              <InfringementsPerEventChart
                data={preAndEventStats.eventBreakdown}
                primaryText={page.primaryText}
                secondaryText={page.secondaryText}
                chartTooltip={utils.chartTooltip}
                title="Infringements Per Day (Pre + Event)"
                subTitle="Post-event rollup of Pre-Event + Event Day totals."
              />
              <PlatformBreakdownChart
                data={postEventAllPlatformBreakdownData}
                primaryText={page.primaryText}
                secondaryText={page.secondaryText}
                chartTooltip={utils.chartTooltip}
                title="Platform Breakdown (Pre + Event)"
                subTitle="All infringing links from Pre-Event + Event Day, grouped by platform."
              />
            </StyledPanel>
            <StyledPanel gap="1rem">
              <Text color={page.primaryText} size="1rem" weight={700}>
                Executive-Level Takeaways
              </Text>
              <ExecutiveTakeawaysPanel
                piracyConcentration={piracyConcentration}
                leverageProviders={leverageProviders}
                top3ProviderTrafficPct={top3ProviderTrafficPct}
                chokePointDomains={chokePointDomains}
                primaryText={page.primaryText}
                secondaryText={page.secondaryText}
                cardBg={page.altBg}
              />
            </StyledPanel>
            <StyledPanel gap="0.5rem">
              <GeoHeatmapPanel
                title="Post-Event Geo Story"
                subtitle="Geographic distribution of traffic and domains/links for post-event reporting."
                data={postEventLinksGeoHeatmapData}
                small={small}
                primaryText={page.primaryText}
                secondaryText={page.secondaryText}
              />
            </StyledPanel>
          </Box>
        )}
        {/* Locked Panels */}
        <Grid columns={small ? ['100%'] : ['1fr', '1fr', '1fr']} gap="1rem">
          {LOCKED_PANELS.map((title) => (
            <StyledPanel
              key={title}
              gap="1rem"
              style={{
                border: `2px dashed ${isDarkMode ? page.tableBorder : page.highlight}`,
                background: page.cardBg,
                boxShadow: 'var(--elevation-active)',
                minHeight: small ? '7.75rem' : '8.5rem',
              }}
            >
              <Box direction="row" gap="0.5rem" align="center">
                <Lock color={page.highlight} size="medium" />
                <Text color={page.primaryText} size="1rem" weight={800}>
                  {title}
                </Text>
              </Box>
              <Text color={page.secondaryText} size="0.9rem" weight={500}>
                Enable Authorized Takedown/Delisting features to view this information.
              </Text>
            </StyledPanel>
          ))}
        </Grid>
        {/* Domain Traffic & Search Engine Pie Charts (all tabs) */}
        <Grid columns={small ? ['100%'] : ['1.3fr', '0.7fr']} gap="1rem">
          <StyledPanel>
            <DomainTrafficChart
              data={topSitesByInfringementsData}
              isEventDay={isEventDay}
              primaryText={page.primaryText}
              secondaryText={page.secondaryText}
              chartTooltip={utils.chartTooltip}
            />
          </StyledPanel>
          <StyledPanel>
            <SearchEnginePieChart
              data={searchEngineData}
              primaryText={page.primaryText}
              secondaryText={page.secondaryText}
              chartTooltip={utils.chartTooltip}
            />
          </StyledPanel>
        </Grid>
        {isEventDay && (
          <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">
            <StyledPanel gap="1rem">
              <Box direction="column" gap="0.5rem">
                <Text color={page.primaryText} size="1rem" weight={700}>
                  Estimated Time-to-Detection (Modeled)
                </Text>
                <Text color={page.secondaryText} size="0.8rem">
                  Stream start time is not directly observed; this is a modeled
                  estimate using first-seen signals and market-density patterns.
                </Text>
              </Box>
              <MetricCard
                label="EST. AVG TIME TO DETECTION (MIN)"
                value={trackerStats.avgTimeToDetection}
                subLabel="Modeled estimate, not exact stream-start timing"
                subLabelColor={page.hintText}
                textColor={page.primaryText}
                cardAccent={page.altBg}
              />
              <StyledChartWrap>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={detectionChartData}>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke="#E6EEF6"
                    />
                    <XAxis
                      dataKey="domain"
                      tick={{ fill: '#5D738A', fontSize: 11 }}
                    />
                    <YAxis tick={{ fill: '#5D738A', fontSize: 11 }} />
                    <Tooltip
                      {...utils.chartTooltip}
                      labelFormatter={(label, payload) => (
                        payload?.[0]?.payload?.fullDomain || label
                      )}
                      formatter={(value) => `${utils.formatInt(value)} min`}
                    />
                    <Bar
                      dataKey="avgMinutesToDetection"
                      fill="#F97316"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </StyledChartWrap>
              <Box gap="0.25rem">
                <Text color={page.secondaryText} size="0.7rem" weight={600}>
                  High-density markets:
                </Text>
                <Text color={page.secondaryText} size="0.7rem">
                  {trackerStats?.highDensityMarkets?.length
                    ? trackerStats.highDensityMarkets
                      .map((item) => item.domain)
                      .join(', ')
                    : 'No high-density markets identified yet.'}
                </Text>
              </Box>
            </StyledPanel>
            <StyledPanel gap="1rem">
              <Box direction="column" gap="0.5rem">
                <Text color={page.primaryText} size="1rem" weight={700}>
                  Top Visited Piracy Sites
                </Text>
                <Text color={page.secondaryText} size="0.8rem">
                  Visits are shown in thousands and overlaid with infringement
                  counts.
                </Text>
                <Text size="0.68rem" color="#B9DCE8">
                  Traffic data sourced from platforms like SimilarWeb and Semrush.
                </Text>
              </Box>
              <MetricCard
                label="TOP SITE MONTHLY VISITORS (K)"
                value={utils.displayOrUnavailable(
                  trafficSeriesData[0]?.monthlyVisitors, utils.formatInt,
                )}
                textColor={page.primaryText}
                cardAccent={page.altBg}
              />
              <StyledChartWrap>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trafficSeriesData}>
                    <defs>
                      <linearGradient
                        id="trafficGradient"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                      >
                        <stop
                          offset="5%"
                          stopColor="#9333EA"
                          stopOpacity={0.65}
                        />
                        <stop
                          offset="95%"
                          stopColor="#9333EA"
                          stopOpacity={0.08}
                        />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E6EEF6" />
                    <XAxis
                      dataKey="domain"
                      tick={{ fill: '#5D738A', fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="left"
                      tick={{ fill: '#5D738A', fontSize: 11 }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      tick={{ fill: '#5D738A', fontSize: 11 }}
                    />
                    <Tooltip
                      {...utils.chartTooltip}
                      labelFormatter={(label, payload) => (
                        payload?.[0]?.payload?.fullDomain || label
                      )}
                      formatter={(value) => utils.formatInt(value)}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="monthlyVisitors"
                      name="Visitors (K)"
                      stroke="#9333EA"
                      fill="url(#trafficGradient)"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="infringements"
                      name="Infringements"
                      stroke="#0EA5A2"
                      strokeWidth={3}
                      dot={{ r: 2 }}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </StyledChartWrap>
            </StyledPanel>
          </Grid>
        )}
        {/* Provider Bar Chart & Infringements Per Event Chart (pre-event and event day) */}
        {!isPostEvent && (
          <Grid columns={small ? ['100%'] : ['1fr', '1fr']} gap="1rem">
            <StyledPanel>
              <ProviderBarChart
                data={providerBarData}
                primaryText={page.primaryText}
                secondaryText={page.secondaryText}
                chartTooltip={utils.chartTooltip}
              />
            </StyledPanel>
            <StyledPanel>
              <InfringementsPerEventChart
                data={kpiStats.eventBreakdown}
                primaryText={page.primaryText}
                secondaryText={page.secondaryText}
                chartTooltip={utils.chartTooltip}
              />
            </StyledPanel>
          </Grid>
        )}
        {/* Geo Heatmap */}
        {!isPostEvent && (
          <StyledPanel>
            <GeoHeatmapPanel
              title="Geographic Spread of Links & Hosting Providers"
              subtitle="Heatmap of link findings and hosting-provider geolocations."
              data={trackerStats.hydratedDomainStats.reduce((acc, item) => {
                const code = utils.getCountryCodeFromLocation(item.ipGeolocation);
                if (code) acc[code] = (acc[code] || 0) + item.count;
                return acc;
              }, {})}
              small={small}
              primaryText={page.primaryText}
              secondaryText={page.secondaryText}
            />
          </StyledPanel>
        )}
        {/* Breakdowns: Language, ISP, Geolocation */}
        <Grid columns={small ? ['100%'] : ['1fr', '1fr', '1fr']} gap="1rem">
          <StyledPanel gap="1rem">
            <Box direction="column" gap="0.25rem">
              <Text color={page.primaryText} size="1rem" weight={700}>By Language</Text>
              <Text color={page.secondaryText} size="0.8rem">Infringement results grouped by detected language.</Text>
            </Box>
            <StyledScrollPanel style={{ maxHeight: '16rem' }}>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Language</th>
                    <th>Results</th>
                  </tr>
                </thead>
                <tbody>
                  {trackerStats.languageBreakdown?.length > 0 ? (
                    trackerStats.languageBreakdown.map((item) => (
                      <tr key={item.language}>
                        <td>{item.language}</td>
                        <td>{utils.formatInt(item.count)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={2}>No language data available.</td></tr>
                  )}
                </tbody>
              </StyledTable>
            </StyledScrollPanel>
          </StyledPanel>
          <StyledPanel gap="1rem">
            <Box direction="column" gap="0.25rem">
              <Text color={page.primaryText} size="1rem" weight={700}>By IP ISP</Text>
              <Text color={page.secondaryText} size="0.8rem">Domains and infringements grouped by hosting provider.</Text>
            </Box>
            <StyledScrollPanel style={{ maxHeight: '16rem' }}>
              <StyledTable>
                <thead>
                  <tr>
                    <th>ISP</th>
                    <th>Domains</th>
                    <th>Results</th>
                  </tr>
                </thead>
                <tbody>
                  {trackerStats.hostingProviderStats?.length > 0 ? (
                    trackerStats.hostingProviderStats.map((item) => (
                      <tr key={item.provider}>
                        <td>{item.provider}</td>
                        <td>{utils.formatInt(item.domains)}</td>
                        <td>{utils.formatInt(item.infringements)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={3}>No ISP data available.</td></tr>
                  )}
                </tbody>
              </StyledTable>
            </StyledScrollPanel>
          </StyledPanel>
          <StyledPanel gap="1rem">
            <Box direction="column" gap="0.25rem">
              <Text color={page.primaryText} size="1rem" weight={700}>By IP Geolocation</Text>
              <Text color={page.secondaryText} size="0.8rem">Infringements grouped by server IP geolocation.</Text>
            </Box>
            <StyledScrollPanel style={{ maxHeight: '16rem' }}>
              <StyledTable>
                <thead>
                  <tr>
                    <th>Territory</th>
                    <th>Results</th>
                  </tr>
                </thead>
                <tbody>
                  {trackerStats.topTerritories?.length > 0 ? (
                    trackerStats.topTerritories.map((item) => (
                      <tr key={item.territory}>
                        <td>{item.territory}</td>
                        <td>{utils.formatInt(item.infringements)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan={2}>No geolocation data available.</td></tr>
                  )}
                </tbody>
              </StyledTable>
            </StyledScrollPanel>
          </StyledPanel>
        </Grid>
        {/* All Domains and Result Totals Table */}
        <Grid
          columns={small ? ['100%'] : [isEventDay ? '1.3fr' : '100%', isEventDay ? '0.7fr' : '0']}
          gap="1rem"
        >
          <StyledPanel gap="1rem">
            <Text color={page.primaryText} size="1rem" weight={700}>
              All Domains and Result Totals
            </Text>
            <TableWrapper
              isPaged={false}
              small={small}
              boxPad="0"
              bgOverride={page.cardBg}
              totalResults={trackerStats?.hydratedDomainStats?.length}
              zeroBasedPageIndex
            >
              <StyledScrollPanel>
                <StyledTable>
                  <thead>
                    <tr>
                      {DOMAIN_HEADER_MAP.map((header) => (!header.eventDayOnly || isEventDay) && (
                        <th key={header.uid} style={{ width: header.size }}>{header.display}</th>
                      ))}
                      {showWatermark && <th>{utils.camelToLabel(watermarkGroupingKey)}</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {trackerStats?.hydratedDomainStats?.map((item) => (
                      <tr key={item.domain}>
                        <td>{item.domain}</td>
                        <td>{utils.formatInt(item.count)}</td>
                        {isEventDay && <td>{utils.formatDateTime(item.firstDetectedAt)}</td>}
                        <td>{item.ipAddress || '—'}</td>
                        <td>{item.ipGeolocation}</td>
                        <td>{item.ipTimestamp ? utils.formatDateTime(item.ipTimestamp) : '—'}</td>
                        <td>{item.ispName}</td>
                        {showWatermark && (
                          <td>{watermarkValuesByDomain[item.domain]?.join(', ') || '—'}</td>
                        )}
                      </tr>
                    ))}
                    {trackerStats?.hydratedDomainStats?.length === 0 && (
                      <tr>
                        <td colSpan={6 + (isEventDay ? 1 : 0) + (showWatermark ? 1 : 0)}>
                          No domain data available yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </StyledTable>
              </StyledScrollPanel>
            </TableWrapper>
          </StyledPanel>
          {/* Top Piracy Sites Intelligence Table (isEventDay only) */}
          {isEventDay && (
            <StyledPanel gap="1rem">
              <Box direction="column" gap="0.35rem">
                <Text color={page.primaryText} size="1rem" weight={700}>
                  Top Piracy Sites Intelligence
                </Text>
                <Text size="0.75rem" color={page.hintText}>
                  Traffic data sourced from platforms like SimilarWeb and Semrush.
                </Text>
              </Box>
              <TableWrapper
                isPaged={false}
                small={small}
                boxPad="0"
                bgOverride={page.cardBg}
                totalResults={trackerStats?.topVisitedSites?.length}
              >
                <StyledScrollPanel>
                  <StyledTable>
                    <thead>
                      <tr>
                        {TOP_SITES_HEADER_MAP.map((header) => (
                          <th key={header.uid} style={{ width: header.size }}>{header.display}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {trackerStats?.topVisitedSites?.map((item) => (
                        <tr key={item.domain}>
                          <td>{item.domain}</td>
                          <td>{utils.formatInt(item.monthlyVisitors)}</td>
                          <td>{item.globalRank ? utils.formatInt(item.globalRank) : 'N/A'}</td>
                        </tr>
                      ))}
                      {trackerStats?.topVisitedSites?.length === 0 && (
                        <tr>
                          <td colSpan={3}>No traffic data available yet.</td>
                        </tr>
                      )}
                    </tbody>
                  </StyledTable>
                </StyledScrollPanel>
              </TableWrapper>
            </StyledPanel>
          )}
        </Grid>
      </Box>
    );
  };

  return (
    <AuthNavWrapper
      smallBanner
      small={small}
      location={location}
      bannerProps={{
        title: 'Live tracker',
        subTitle: 'Analytics',
        bannerChildren: (
          <Box direction="row" align="center" gap="0.75rem" wrap>
            {visibleTabs.length > 1 && (
              <Box
                direction="row"
                gap="0.35rem"
                background={page.altBg}
                pad="0.35rem"
                round="var(--app-radius)"
                width="fit-content"
              >
                {visibleTabs.map((tab) => (
                  <Box
                    as="button"
                    type="button"
                    key={tab.key}
                    pad={{ horizontal: '0.75rem', vertical: '0.55rem' }}
                    round="var(--app-radius)"
                    background={activeTab === tab.key ? useActiveSliderBg : 'transparent'}
                    disabled={loading}
                    style={{
                      border: 'none',
                      color: page.primaryText,
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      cursor: 'pointer',
                    }}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </Box>
                ))}
              </Box>
            )}
            <Box
              as="button"
              type="button"
              direction="row"
              align="center"
              gap="0.4rem"
              pad={{ horizontal: '0.85rem', vertical: '0.55rem' }}
              round="var(--app-radius)"
              background={page.highlight}
              disabled={isExporting || loading}
              onClick={exportToPDF}
              style={{
                border: 'none',
                cursor: isExporting || loading ? 'not-allowed' : 'pointer',
                opacity: isExporting || loading ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
              }}
            >
              {isExporting ? (
                <Spinning size="small" color={button.primaryText} />
              ) : (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke={button.primaryText}
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="7 10 12 15 17 10" />
                  <line x1="12" y1="15" x2="12" y2="3" />
                </svg>
              )}
              <Text size="0.85rem" weight={600} color={button.primaryText}>
                {isExporting ? 'Exporting...' : 'Export PDF'}
              </Text>
            </Box>
          </Box>
        ),
        bannerChildrenPosition: 'bottom',
      }}
    >
      <Seo />
      {/* PDF-only header — always mounted regardless of active tab */}
      <Box
        ref={pdfHeaderRef}
        direction="row"
        align="center"
        justify="between"
        pad={{ horizontal: small ? '0.75rem' : '2rem', vertical: '1.25rem' }}
        background={isDarkMode ? page.background : page.altBg}
        style={{
          position: 'fixed',
          top: '-9999px',
          left: 0,
          borderBottom: '1px solid var(--page-table-border)',
        }}
      >
        <Box direction="column" gap="0.15rem">
          <Text
            size="0.7rem"
            weight={600}
            color={page.secondaryText}
            style={{ letterSpacing: '0.1em', textTransform: 'uppercase' }}
          >
            Analytics
          </Text>
          <Text size="1.5rem" weight={700} color={page.primaryText}>
            Live tracker
          </Text>
        </Box>
        {visibleTabs.length > 1 && (
          <Box
            direction="row"
            gap="0.35rem"
            background={page.altBg}
            pad="0.35rem"
            round="var(--app-radius)"
          >
            {visibleTabs.map((tab) => (
              <Box
                key={tab.key}
                pad={{ horizontal: '0.75rem', vertical: '0.55rem' }}
                round="var(--app-radius)"
                background={activeTab === tab.key ? useActiveSliderBg : 'transparent'}
              >
                <Text size="0.85rem" weight={600} color={page.primaryText}>
                  {tab.label}
                </Text>
              </Box>
            ))}
          </Box>
        )}
      </Box>
      {renderPageContents()}
      <ScrollToTopButton
        overrideHover
        bg={page.highlight}
        color={button.primaryText}
        tooltipPosition="left"
        threshold={300}
      />
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    infringementsData: state.productResults.infringements,
    hostingData: state.analytics.liveTrackerData,
    watermarkDetectionsData: state.analytics.watermarkDetections,
    loading: state.fetchLoader.dataLoading,
    hostingDataLoading: state.fetchLoader.secondaryDataLoading,
    managementReportsAccess: state.auth.managementReportsAccess,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchInfringements: initiateResultsInfringementsRequest,
    clearInfringements: clearResultsInfringementsRequest,
    fetchHostingData: initiateLiveTrackerAnalyticsRequest,
    clearHostingData: clearLiveTrackerAnalyticsRequest,
    fetchWatermarkDetections: initiateWatermarkDetectionsRequest,
    clearWatermarkDetections: clearWatermarkDetectionsRequest,
  }, dispatch);
}

LiveTrackerPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  infringementsData: PropTypes.object,
  hostingData: PropTypes.object,
  watermarkDetectionsData: PropTypes.array,
  loading: PropTypes.bool.isRequired,
  hostingDataLoading: PropTypes.bool.isRequired,
  managementReportsAccess: PropTypes.bool.isRequired,
  fetchInfringements: PropTypes.func.isRequired,
  clearInfringements: PropTypes.func.isRequired,
  fetchHostingData: PropTypes.func.isRequired,
  clearHostingData: PropTypes.func.isRequired,
  fetchWatermarkDetections: PropTypes.func.isRequired,
  clearWatermarkDetections: PropTypes.func.isRequired,
  featureConfig: PropTypes.shape({
    liveTrackerEstimatedSubscriptionFee: PropTypes.number,
    watermarkGroupingKey: PropTypes.string,
    showPrePostEventAnalysis: PropTypes.bool,
    liveTrackerWatermarkGroupingKey: PropTypes.string,
  }).isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductAuth(LiveTrackerPage));
